import {
  CANVAS_SESSION_STORAGE_PREFIX,
  type CanvasSessionRecord,
} from './canvas-session-types';

export interface RecentSite {
  siteId: string;
  title: string;
  updatedAt: number;
}

const ACTIVE_SUFFIX = 'active';

/** Human title for a session (business name → siteId fallback). */
export function siteTitleFromSession(record: Pick<CanvasSessionRecord, 'draft' | 'siteId'>): string {
  const name = record.draft?.businessName?.trim();
  if (name) {
    return name;
  }
  return record.siteId ?? 'Untitled site';
}

/**
 * Real recent sites from persisted canvas sessions (localStorage). No fake
 * seed data — returns `[]` until the user has generated/opened sites. Sorted
 * newest-first and de-duplicated by siteId.
 */
export function getRecentSites(limit = 3): RecentSite[] {
  if (typeof window === 'undefined') {
    return [];
  }

  const byId = new Map<string, RecentSite>();

  for (let i = 0; i < window.localStorage.length; i += 1) {
    const key = window.localStorage.key(i);
    if (!key || !key.startsWith(CANVAS_SESSION_STORAGE_PREFIX)) {
      continue;
    }
    if (key === `${CANVAS_SESSION_STORAGE_PREFIX}${ACTIVE_SUFFIX}`) {
      continue;
    }

    const raw = window.localStorage.getItem(key);
    if (!raw) {
      continue;
    }

    try {
      const parsed = JSON.parse(raw) as CanvasSessionRecord;
      if (!parsed || typeof parsed !== 'object' || !parsed.siteId) {
        continue;
      }
      const existing = byId.get(parsed.siteId);
      const updatedAt = typeof parsed.updatedAt === 'number' ? parsed.updatedAt : 0;
      if (!existing || updatedAt > existing.updatedAt) {
        byId.set(parsed.siteId, {
          siteId: parsed.siteId,
          title: siteTitleFromSession(parsed),
          updatedAt,
        });
      }
    } catch {
      // Skip corrupt entries — never throw from a sidebar read.
    }
  }

  return Array.from(byId.values())
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, Math.max(0, limit));
}
